import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { FlyerType } from '@/config/constants';
import colors from '@/theme/colors';
import Tooltip from '@mui/material/Tooltip';
import { BodyLargeEmphasized, BodyLargeMidEmphasis } from '../Typography';
import styles from './ContentSwitcher.style';

type ContentSwicherOption = {
  value: any;
  label: string;
  badge?: number;
  badgeLabel?: string;
};
interface Props {
  trackId: string;
  options: ContentSwicherOption[];
  selected: any;
  onChange: (newValue: string) => void;
  disabledTab?: FlyerType.school | FlyerType.community;
  tabGroupId?: string;
}

const SCHOOL_DISABLED_TOOLTIP = 'These type of flyers do not apply to schools.';
const COMMUNITY_DISABLED_TOOLTIP = 'These type of flyers do not apply to the community.';
const DISABLED_TOOLTIP_DESC_ID = 'disabled-tab-tooltip-desc';

export const ContentSwitcher: React.FC<Props> = ({
  trackId,
  selected,
  options,
  onChange,
  disabledTab,
  tabGroupId,
}) => {
  const { track } = useAnalytics();

  const tabPanelDomId = tabGroupId ? `${tabGroupId}-tabpanel` : undefined;
  const tabDomIdPrefix = tabGroupId ? `${tabGroupId}-tab` : undefined;

  const getTabA11yProps = (value: ContentSwicherOption['value']) =>
    tabPanelDomId
      ? {
          id: `${tabDomIdPrefix}-${value}`,
          'aria-controls': tabPanelDomId,
        }
      : {};
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (disabledTab) {
      return;
    }

    if (trackId) {
      track({
        action: GenericTrackTypes.TAB_NAVIGATION,
        category: `${trackId}:switcher`,
        label: newValue,
      });
    }

    onChange(newValue);
  };

  const tooltipText =
    disabledTab === FlyerType.school ? SCHOOL_DISABLED_TOOLTIP : COMMUNITY_DISABLED_TOOLTIP;

  const renderLabel = (
    { label, value, badge, badgeLabel = 'items' }: ContentSwicherOption,
    isTabDisabled = false
  ) => {
    const isSelected = selected === value;
    const text = isSelected ? (
      <BodyLargeEmphasized color={colors.jungle}>{label}</BodyLargeEmphasized>
    ) : (
      <BodyLargeMidEmphasis color={isTabDisabled ? colors.stone : colors.prussian}>
        {label}
      </BodyLargeMidEmphasis>
    );

    if (badge == null) return text;

    return (
      <span className={styles.tabLabel}>
        {text}
        <span className={styles.badge} aria-label={`${badge} ${badgeLabel}`}>
          {badge}
        </span>
      </span>
    );
  };

  const renderTabWithTooltip = (option: ContentSwicherOption) => {
    const { label, value } = option;
    const isTabDisabled = value === disabledTab;
    return (
      <Tooltip
        title={isTabDisabled ? tooltipText : ''}
        arrow
        enterTouchDelay={0}
        componentsProps={{
          popper: {
            sx: {
              [`& .MuiTooltip-arrow`]: {
                color: colors.midnight,
              },
              [`& .MuiTooltip-tooltip`]: {
                backgroundColor: colors.midnight,
                fontSize: '14px',
                borderRadius: '5px',
              },
            },
          },
        }}
      >
        <Tab
          key={value}
          value={value}
          {...getTabA11yProps(value)}
          label={renderLabel(option, isTabDisabled)}
          className={styles.tab}
          data-testid={`tab${label}`}
          aria-describedby={isTabDisabled ? DISABLED_TOOLTIP_DESC_ID : undefined}
        />
      </Tooltip>
    );
  };

  const renderTab = (option: ContentSwicherOption) => (
    <Tab
      key={option.value}
      value={option.value}
      {...getTabA11yProps(option.value)}
      label={renderLabel(option)}
      className={styles.tab}
      data-testid={`tab${option.label}`}
    />
  );

  const contentSwicherTab = disabledTab ? renderTabWithTooltip : renderTab;

  return (
    <>
      {disabledTab && (
        <span id={DISABLED_TOOLTIP_DESC_ID} className="sr-only">
          {tooltipText}
        </span>
      )}
      <Tabs
        value={selected}
        onChange={handleChange}
        className={styles.container}
        variant="fullWidth"
        data-testid="tabHeader"
        aria-label="Flyer type switcher"
        selectionFollowsFocus
      >
        {options.map(contentSwicherTab)}

        {disabledTab && (
          <span
            className={styles.tabIndicator}
            style={selected === FlyerType.school ? { left: 0 } : { right: 0 }}
          />
        )}
      </Tabs>
    </>
  );
};
